import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { API_URL } from '../config/api';
import { 
  X, Bell, BellOff, Calendar, PiggyBank, Target, 
  AlertTriangle, Cake, FileText, Check
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = API_URL;
const API = `${BACKEND_URL}/api`;

const NotificationSettings = ({ open, onClose, user }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState({
    enabled: false,
    recurring_expense_reminder: true,
    savings_reminder: true,
    goal_deadline_reminder: true,
    budget_alert: true,
    birthday_notification: true,
    monthly_summary: true,
    reminder_days: [7, 3, 1, 0],
    summary_day: 1
  });

  useEffect(() => {
    if (open) {
      loadPreferences();
    }
  }, [open]);

  const loadPreferences = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/notification-preferences`, { withCredentials: true });
      setPreferences(prev => ({
        ...prev,
        ...response.data,
        reminder_days: response.data.reminder_days || [7, 3, 1, 0]
      }));
    } catch (error) {
      console.error('Error loading preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/notification-preferences`, preferences, { withCredentials: true });
      toast.success('Préférences sauvegardées ! 🔔');
      onClose();
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const toggleType = (type) => {
    setPreferences(prev => ({ ...prev, [type]: !prev[type] }));
  };

  const toggleReminderDay = (day) => {
    setPreferences(prev => {
      const days = prev.reminder_days.includes(day)
        ? prev.reminder_days.filter(d => d !== day)
        : [...prev.reminder_days, day].sort((a, b) => b - a);
      return { ...prev, reminder_days: days };
    });
  };

  if (!open) return null;

  const notificationTypes = [
    { key: 'recurring_expense_reminder', label: 'Dépenses récurrentes', icon: Calendar, desc: 'Rappel avant prélèvement' },
    { key: 'savings_reminder', label: 'Épargne automatique', icon: PiggyBank, desc: 'Rappel versement épargne' },
    { key: 'goal_deadline_reminder', label: 'Échéance objectif', icon: Target, desc: 'Rappel date cible' },
    { key: 'budget_alert', label: 'Alerte budget', icon: AlertTriangle, desc: 'Dépassement détecté' },
    { key: 'birthday_notification', label: 'Anniversaire', icon: Cake, desc: 'Message personnalisé' },
    { key: 'monthly_summary', label: 'Bilan mensuel', icon: FileText, desc: 'Résumé du mois' }
  ];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
        
        {/* Content */}
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 25 }}
          className="relative w-full sm:max-w-md max-h-[90vh] bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-4 sticky top-0 z-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <Bell className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Notifications</h2>
                  <p className="text-xs text-white/70">Notifications push</p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10 transition-colors">
                <X className="h-5 w-5 text-white" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="overflow-y-auto p-4 space-y-4" style={{ maxHeight: 'calc(90vh - 160px)' }}>
            {loading ? (
              <div className="flex items-center justify-center py-10">
                <motion.div 
                  className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                />
              </div>
            ) : (
              <>
                {/* Master Toggle */}
                <div className={`rounded-xl p-4 ${preferences.enabled ? 'bg-emerald-50 border border-emerald-200' : 'bg-gray-50 border border-gray-200'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {preferences.enabled ? (
                        <Bell className="h-6 w-6 text-emerald-600" />
                      ) : (
                        <BellOff className="h-6 w-6 text-gray-400" />
                      )}
                      <div>
                        <h3 className="font-semibold text-gray-900">Notifications push</h3>
                        <p className="text-xs text-gray-500">
                          {preferences.enabled ? 'Activées - Tu recevras des alertes dans l\'app' : 'Désactivées'}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={preferences.enabled}
                      onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, enabled: checked }))}
                    />
                  </div>
                </div>

                {preferences.enabled && (
                  <>
                    {/* Consent Notice */}
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                      <p className="text-xs text-blue-700">
                        🔒 Tes préférences sont sauvegardées localement. Les notifications sont générées uniquement dans l'application, sans envoi externe.
                      </p>
                    </div>

                    {/* Notification Types */}
                    <div className="space-y-2">
                      <h3 className="text-sm font-semibold text-gray-700 px-1">Types de notifications</h3>
                      {notificationTypes.map((type) => (
                        <motion.div
                          key={type.key}
                          className={`rounded-xl p-3 border transition-colors ${
                            preferences[type.key] 
                              ? 'bg-white border-teal-200' 
                              : 'bg-gray-50 border-gray-100'
                          }`}
                          whileTap={{ scale: 0.98 }}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                preferences[type.key] ? 'bg-teal-100 text-teal-600' : 'bg-gray-100 text-gray-400'
                              }`}>
                                <type.icon className="h-4 w-4" />
                              </div>
                              <div>
                                <h4 className="text-sm font-medium text-gray-900">{type.label}</h4>
                                <p className="text-xs text-gray-500">{type.desc}</p>
                              </div>
                            </div>
                            <Switch
                              checked={preferences[type.key]}
                              onCheckedChange={() => toggleType(type.key)}
                            />
                          </div>
                        </motion.div>
                      ))}
                    </div>

                    {/* Reminder Days */}
                    <div className="bg-gray-50 rounded-xl p-4">
                      <h3 className="text-sm font-semibold text-gray-700 mb-3">Rappels avant échéance</h3>
                      <div className="flex flex-wrap gap-2">
                        {[7, 3, 1, 0].map((day) => (
                          <button
                            key={day}
                            onClick={() => toggleReminderDay(day)}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              preferences.reminder_days.includes(day)
                                ? 'bg-teal-500 text-white'
                                : 'bg-white border border-gray-200 text-gray-600'
                            }`}
                          >
                            {day === 0 ? 'Jour J' : `J-${day}`}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Summary Day */}
                    <div className="bg-gray-50 rounded-xl p-4">
                      <h3 className="text-sm font-semibold text-gray-700 mb-3">Jour du bilan mensuel</h3>
                      <Select
                        value={preferences.summary_day.toString()}
                        onValueChange={(value) => setPreferences(prev => ({ ...prev, summary_day: parseInt(value) }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1er du mois</SelectItem>
                          <SelectItem value="15">15 du mois</SelectItem>
                          <SelectItem value="28">28 du mois</SelectItem>
                          <SelectItem value="0">Dernier jour du mois</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t bg-gray-50">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
            >
              {saving ? 'Sauvegarde...' : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Sauvegarder
                </>
              )}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default NotificationSettings;
